export const billingConfiguration = {
  header: {
    title: "Configuration de la facturation",
    description:
      "Gérez les émetteurs de facturation de votre compte et associez chaque hébergement au bon émetteur.",
  },

  intro: {
    lead:
      "Un <strong>émetteur de facturation</strong> est l'entité fiscale qui facture vos clients. Vous pouvez en avoir plusieurs et choisir lequel facture chaque hébergement.",
    feats: {
      fiscalData: {
        label: "Données fiscales propres",
        tip: "Raison sociale, NIF/CIF et adresse fiscale figurant sur la facture.",
      },
      series: {
        label: "Séries indépendantes",
        tip: "Chaque émetteur conserve sa propre série et sa propre séquence (ainsi que pour les factures rectificatives), jamais mélangées.",
      },
      verifactu: {
        label: "VeriFactu par émetteur",
        tip: "Chaque émetteur respecte VeriFactu (signature et enregistrement auprès de l'AEAT) séparément, selon sa configuration fiscale.",
      },
      assignment: {
        label: "Attribution par hébergement",
        tip: "Associez chaque hébergement à un émetteur. Ceux qui ne sont pas attribués utilisent l'émetteur principal.",
      },
    },
    foot: "Lors de l'émission, l'émetteur de l'hébergement de la réservation est utilisé (au check-out ou lorsque le client le demande à l'agent IA).",
  },

  issuers: {
    card: {
      title: "Émetteurs de facturation",
      subtitle: "Créez plusieurs émetteurs et attribuez vos hébergements à chacun d'eux.",
    },
    searchPlaceholder: "Rechercher un émetteur par nom ou NIF…",
    newButton: "Nouvel émetteur",
    noName: "Sans nom",
    defaultBadge: "Principal",
    listingsCount: "{count} hébergements",
    empty: {
      noMatch: "Aucun émetteur ne correspond à votre recherche.",
      none: "Vous n'avez encore aucun émetteur. Créez le premier avec “Nouvel émetteur”.",
    },
  },

  assign: {
    card: {
      title: "Attribution des hébergements",
      subtitle:
        "Choisissez quel émetteur facture chaque hébergement. Un hébergement ne peut avoir qu'un seul émetteur ; ceux qui ne sont pas attribués utilisent le principal.",
    },
    searchPlaceholder: "Rechercher un hébergement…",
    filter: {
      all: "Tous",
      unassigned: "Non attribués",
    },
    issuerFallback: "Émetteur n° {id}",
    principalSuffix: " (principal)",
    unassignedOption: "— Non attribué (principal)",
    empty: {
      noMatch: "Aucun hébergement ne correspond au filtre.",
      none: "Vous n'avez encore aucun hébergement.",
    },
    count: "{shown} sur {total} hébergements",
  },

  drawer: {
    editTitle: "Modifier l'émetteur",
    newTitle: "Nouvel émetteur",
    subtitle: "Données fiscales de l'émetteur de facturation",
    closeAria: "Fermer",
    footer: {
      isDefault: "C'est l'émetteur principal",
      setDefault: "Définir comme principal",
      delete: "Supprimer",
      cancel: "Annuler",
      saving: "Enregistrement...",
      save: "Enregistrer l'émetteur",
    },
  },

  company: {
    title: "Données de l'entreprise",
    subtitle: "Informations fiscales de votre propriété",
    country: {
      label: "Pays de facturation",
      hint: "Choisissez le pays fiscal applicable à cette configuration.",
    },
    personType: {
      label: "Type d'entité",
      company: "Personne morale (entreprise)",
      individual: "Personne physique (travailleur indépendant)",
    },
    legalName: {
      label: "Nom / Raison sociale",
      placeholderCompany: "p. ex. INNDEV S.L.",
      placeholderIndividual: "p. ex. María García",
      hint: "Nom fiscal de l'entreprise ou de la personne physique",
    },
    co: {
      regime: {
        label: "Régime DIAN",
        common: "Régime commun",
        simplified: "Régime simplifié",
        hint: "Requis pour la facturation électronique DIAN",
      },
      municipality: {
        label: "Municipalité DIAN",
        hint: "Code DIVIPOLA de votre municipalité — requis par la DIAN.",
        selected: "Sélectionné : {code}",
      },
      responsibilities: {
        label: "Obligations fiscales",
        placeholder: "p. ex. O-13, R-99-PN",
        hint: "Codes séparés par des virgules. Le plus courant : <strong>O-13</strong>",
      },
    },
    mx: {
      regime: {
        label: "Régime fiscal (SAT)",
        placeholder: "p. ex. 601",
        hint: "Code de régime fiscal SAT",
      },
      zip: {
        label: "Code postal fiscal (SAT)",
        placeholder: "p. ex. 06600",
        hint: "Code postal fiscal SAT",
      },
    },
  },

  address: {
    title: "Adresse fiscale",
    subtitle: "L'adresse figurant sur les factures",
    street: {
      label: "Rue et numéro",
    },
    city: {
      label: "Ville",
      hint: "Ville et code postal",
    },
    email: {
      label: "E-mail de facturation",
      placeholder: "billing@yourproperty.com",
    },
    phone: {
      label: "Téléphone",
    },
    ph: {
      ES: {
        street: "p. ex. 42 Gran Vía, étage 3",
        city: "Madrid",
        zip: "28001",
        regionLabel: "Province",
        region: "Madrid",
        phone: "+34 123 456 789",
      },
      CO: {
        street: "p. ex. Calle 13 No. 10-25",
        city: "Bogotá",
        zip: "110111",
        regionLabel: "Département",
        region: "Cundinamarca",
        phone: "+57 601 234 5678",
      },
      MX: {
        street: "p. ex. Av. Insurgentes Sur 1234",
        city: "Mexico",
        zip: "06600",
        regionLabel: "État",
        region: "CDMX",
        phone: "+52 55 1234 5678",
      },
    },
  },

  bank: {
    title: "Compte pour les paiements",
    subtitle: "Où l'on vous verse les virements",
    iban: {
      label: "Numéro de compte (IBAN)",
      placeholder: "FR14 2004 1010 0505 0001 3M02 606",
      hint: "N'apparaît sur la facture que si le mode de paiement est le virement.",
      error: "Le numéro de compte (IBAN) n'est pas valide.",
    },
    bic: {
      label: "BIC / SWIFT",
      placeholder: "PSSTFRPP",
      hint: "Facultatif. Nécessaire pour les paiements hors zone SEPA.",
    },
    holder: {
      label: "Titulaire du compte",
      placeholder: "Laissez vide si identique à la raison sociale",
      hint: "Uniquement si le compte est à un nom différent de celui de l'entreprise.",
    },
  },

  invoice: {
    title: "Configuration des factures",
    subtitle: "Format, devise et numérotation de vos documents",
    currency: {
      label: "Devise par défaut",
      hint: "Devise dans laquelle les factures seront émises",
    },
    taxRate: {
      label: "% d'IVA par défaut",
      hintCO: "IVA général Colombie : <strong>19%</strong>",
      hintMX: "IVA général Mexique : <strong>16%</strong>",
      hintES: "IVA hôtellerie réduit Espagne : <strong>10%</strong> — général : <strong>21%</strong>",
    },
    type: {
      label: "Type de facture",
      detailed: {
        label: "Détaillée",
        desc: "Chaque service sur sa propre ligne",
      },
      simplified: {
        label: "Simplifiée",
        desc: "Reçu ou facture avec éléments regroupés",
      },
    },
    numbering: {
      label: "Numérotation des documents",
      example: "p. ex. {value}",
      invoices: {
        title: "Factures",
      },
      series: "Série",
      optional: "(facultatif)",
      optionalPlaceholder: "Facultatif",
      digits: "Chiffres",
      nextNumber: "Prochain numéro",
      hintOptional: "La série est facultative. Si elle est laissée vide, la facture sera émise avec le numéro séquentiel uniquement.",
      hintFormat: "Si vous définissez une série, le système construira le code sous la forme SÉRIE-NUMÉRO. Sans série, il produira uniquement le NUMÉRO.",
      hintWarn: "Définissez cette valeur avant d'émettre la première facture de la série afin de conserver la séquence fiscale alignée.",
      hintSandboxCO: "En environnement sandbox, utilisez <strong>SETP</strong> — en production, cela doit correspondre à votre résolution DIAN.",
    },
    creditNotes: {
      title: "Avoirs",
      badge: "Annulations / corrections",
      hint: "Utilisé pour le prochain avoir ou la prochaine annulation que vous émettez.",
    },
    footer: {
      label: "Pied de page par défaut",
      hint: "Texte qui apparaîtra au bas de toutes les factures",
      phES: "p. ex. Opération soumise à l'IVA hôtellerie réduit conformément à l'art. 91 de la loi sur l'IVA",
      phCO: "p. ex. Résolution DIAN n° 000123 du 01-01-2025. Plages de 1 à 5000000",
      phMX: "p. ex. CFDI émis conformément aux exigences de l'article 29-A du CFF",
    },
  },

  advanced: {
    title: "Options avancées",
    subtitle: "Paramètres d'envoi supplémentaires",
    attachPdf: {
      label: "Joindre le PDF",
      text: "Inclure le document PDF en plus du lien de téléchargement",
    },
  },

  loading: {
    default: "Chargement...",
    config: "Chargement de la configuration...",
    profiles: "Chargement des émetteurs...",
    saving: "Enregistrement de la configuration...",
  },

  countries: {
    ES: "Espagne",
    CO: "Colombie",
    MX: "Mexique",
  },

  currencies: {
    EUR: "Euro",
    COP: "Peso colombien",
    USD: "Dollar américain",
    MXN: "Peso mexicain",
  },

  taxId: {
    labelFallback: "Identifiant fiscal",
    helpES: "NIF pour les particuliers, CIF pour les entreprises. p. ex. B12345678",
    helpCO: "NIT avec chiffre de contrôle. p. ex. 9014514812 (10 chiffres)",
    helpMX: "RFC de 12 ou 13 caractères. p. ex. XAXX010101000",
    helpFallback: "Identifiant fiscal",
  },

  validation: {
    taxIdES: "Format invalide. p. ex. B12345678",
    taxIdCO: "Le NIT doit comporter 9 ou 10 chiffres. p. ex. 9014514812",
    taxIdMX: "Format de RFC invalide. p. ex. XAXX010101000",
  },

  msg: {
    reassignError: "Impossible de réattribuer l'hébergement.",
    defaultUpdated: "Émetteur principal mis à jour.",
    setDefaultError: "Impossible de définir comme principal.",
    deleteConfirm: "Supprimer cet émetteur de facturation ?",
    deleted: "Émetteur supprimé.",
    deleteError: "Impossible de supprimer l'émetteur.",
    requiredFields: "Complétez tous les champs obligatoires avant d'enregistrer.",
    fixTaxId: "Corrigez l'identifiant fiscal avant d'enregistrer.",
    coRegimeRequired: "Le régime DIAN est obligatoire pour la Colombie.",
    coMunicipalityRequired: "La municipalité DIAN est obligatoire pour la Colombie.",
    savedOk: "Émetteur enregistré avec succès.",
    saveError: "Erreur lors de l'enregistrement de la configuration.",
  },

  misc: {
    thisIssuer: "cet émetteur",
  },
};
